import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { GET_ELIGIBLE_CONTACT_PEOPLE } from "../../../../utils/Controller/URLConstants";

export function contactIdentity(contactPerson) {
  if (!contactPerson?.userId) return null;
  return `${contactPerson.userId}:${contactPerson.workplaceId ?? ""}`;
}

export function selectApplicationContact(
  people = [],
  currentContact = null,
  random = Math.random,
) {
  if (people.length === 0) return currentContact;

  const currentIdentity = contactIdentity(currentContact);
  const currentMatch = currentIdentity
    ? people.find((person) => contactIdentity(person) === currentIdentity)
    : null;
  if (currentMatch) return currentMatch;

  return people[Math.floor(random() * people.length)] ?? currentContact;
}

function fallbackContactFromJob(jobData) {
  if (!jobData?.contactPerson) return null;
  const workplace = jobData.workplaceDetails ?? {};
  const contactData = jobData.contactPersonData ?? {};

  return {
    userId: jobData.contactPerson,
    workplaceId: workplace.workPlaceId ?? "",
    firstName: contactData.firstName ?? "",
    lastName: contactData.lastName ?? "",
    profileImageUrl: contactData.profileImageUrl ?? "",
    workplaceName: workplace.name ?? workplace.name_obj ?? "",
    workplaceLogoUrl: workplace.logoUrl ?? "",
  };
}

export function useApplicationContact({
  jobId,
  jobData,
  currentUser,
  authProfile,
  applicationEmail,
  sharedBy,
  sharedTenantId,
}) {
  const jobFallback = useMemo(
    () => fallbackContactFromJob(jobData),
    [jobData],
  );
  const initialContact = jobFallback;
  const [contactPerson, setContactPerson] = useState(initialContact);
  const [eligibleContactPeople, setEligibleContactPeople] = useState([]);
  const [status, setStatus] = useState("idle");
  const contactRef = useRef(initialContact);
  const requestRef = useRef(null);
  const requestKeyRef = useRef(null);
  const activeJobIdRef = useRef(jobId);

  const updateContact = useCallback((nextContact) => {
    contactRef.current = nextContact;
    setContactPerson(nextContact);
    return nextContact;
  }, []);

  useEffect(() => {
    if (activeJobIdRef.current === jobId) return;

    activeJobIdRef.current = jobId;
    requestRef.current = null;
    requestKeyRef.current = null;
    setEligibleContactPeople([]);
    updateContact(jobFallback);
    setStatus("idle");
  }, [jobId, jobFallback, updateContact]);

  useEffect(() => {
    if (!contactRef.current && jobFallback) {
      updateContact(jobFallback);
    }
  }, [jobFallback, updateContact]);

  const loadContactPerson = useCallback(
    ({ force = false } = {}) => {
      if (!jobId) return Promise.resolve(contactRef.current);

      const candidateEmail = applicationEmail || currentUser?.email ||
        authProfile?.email || "";
      const requestKey = [
        jobId,
        currentUser?.uid ?? "",
        candidateEmail,
        sharedBy ?? "",
        sharedTenantId ?? "",
      ].join(":");

      if (
        !force &&
        requestRef.current &&
        requestKeyRef.current === requestKey
      ) {
        return requestRef.current;
      }

      requestKeyRef.current = requestKey;
      setStatus("loading");
      const request = axios
        .post(
          GET_ELIGIBLE_CONTACT_PEOPLE,
          {
            jobId,
            userId: currentUser?.uid || undefined,
            email: candidateEmail || undefined,
            sharedBy: sharedBy || undefined,
            tenantId: sharedTenantId || undefined,
          },
        )
        .then((response) => {
          if (requestKeyRef.current !== requestKey) {
            return contactRef.current;
          }

          if (response.data?.status !== 0) {
            setStatus(contactRef.current ? "resolved" : "error");
            return contactRef.current;
          }

          const people = response.data.contactPeople ?? [];
          const preferredContact = people.find(
            (person) => person.userId ===
              response.data.preferredContactPersonId,
          );
          const selected = selectApplicationContact(
            people,
            preferredContact ?? contactRef.current ?? jobFallback,
          );
          updateContact(selected);
          setEligibleContactPeople(people);
          setStatus(selected ? "resolved" : "empty");
          return selected;
        })
        .catch((error) => {
          if (requestKeyRef.current === requestKey) {
            console.error("Error loading application contact person:", error);
            setStatus(contactRef.current ? "resolved" : "error");
          }
          return contactRef.current;
        });

      requestRef.current = request;
      return request;
    },
    [
      jobId,
      currentUser?.uid,
      currentUser?.email,
      authProfile?.email,
      applicationEmail,
      sharedBy,
      sharedTenantId,
      jobFallback,
      updateContact,
    ],
  );

  useEffect(() => {
    if (!jobId) return;
    loadContactPerson();

    return () => {
      requestKeyRef.current = null;
    };
  }, [jobId, loadContactPerson]);

  const ensureContactPerson = useCallback(async () => {
    if (contactRef.current?.userId && status !== "error") {
      return contactRef.current;
    }

    const selected = await loadContactPerson();
    if (selected?.userId) return selected;
    return loadContactPerson({ force: true });
  }, [loadContactPerson, status]);

  const selectContactPerson = useCallback((nextContact) => {
    const eligibleContact = eligibleContactPeople.find(
      (person) => contactIdentity(person) === contactIdentity(nextContact),
    );
    return eligibleContact ? updateContact(eligibleContact) : contactRef.current;
  }, [eligibleContactPeople, updateContact]);

  return {
    contactPerson,
    status,
    ensureContactPerson,
    eligibleContactPeople,
    canChangeContactPerson: eligibleContactPeople.length > 1,
    selectContactPerson,
  };
}

export default useApplicationContact;
